import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import { PageHeader } from '@pushly/aqe/lib/components'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { CatalogDataView } from './catalog-data-view'
import { CatalogItemType } from './types'
import { resolveInitialLocale, CATALOG_LOCALE_PREF_KEY } from './locale-utils'
import SimpleTabbedView from '../../components/simple-tabbed-view/simple-tabbed-view'
import './catalog.scss'

/**
 * Capitalizes the first letter of a string.
 */
function capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * CatalogPage component - Root page component for the Catalog Browser feature.
 *
 * Responsibilities:
 * - Read itemType from appState.currentDomain.ecommConfig.itemType
 * - Redirect to dashboard if itemType not configured
 * - Render page header with dynamic title: "{ItemType} Catalog"
 * - Render CatalogDataView in a tabbed layout for future Item Groups tab
 */
export const CatalogPage: React.FC = observer(() => {
    const appState = Container.get(AppState)
    const appService = Container.get(AppService)

    const domain = appState.currentDomain
    const itemType = domain?.ecommConfig?.itemType as CatalogItemType | undefined
    const catalogLocales = domain?.ecommConfig?.catalogLocales
    const catalogDefaultLocale = domain?.ecommConfig?.catalogDefaultLocale
    const hasMultipleLocales = !!catalogLocales && catalogLocales.length > 1

    const userPreferences = useUserPreferences(appState.currentUser?.id, domain?.id)

    const [selectedLocale, setSelectedLocale] = useState<string | undefined>(() => {
        if (!hasMultipleLocales || !catalogLocales) return undefined
        return resolveInitialLocale(catalogLocales, catalogDefaultLocale, userPreferences)
    })

    const handleLocaleChange = (locale: string) => {
        setSelectedLocale(locale)
        userPreferences.set([CATALOG_LOCALE_PREF_KEY], locale)
    }

    useEffect(() => {
        if (domain && !itemType) {
            appService.routeWithinDomain('/dashboard')
        }
    }, [domain?.id, itemType, appService])

    // Must be called unconditionally (before any early return) because
    // wrapComponentTab uses React.useMemo internally — a hook.
    const CatalogTab = SimpleTabbedView.wrapComponentTab(CatalogDataView, {
        tabName: null,
        tabLabel: 'Catalog',
    })

    if (!domain || !itemType) {
        return null
    }

    const itemTypeLabel = capitalizeFirstLetter(itemType)
    const pageTitle = `${itemTypeLabel} Catalog`

    const localeProps = hasMultipleLocales
        ? {
              locale: selectedLocale,
              onLocaleChange: handleLocaleChange,
              catalogLocales,
          }
        : {}

    const catalogTabs = [
        {
            tabClassName: 'catalog-list-tab',
            component: CatalogTab,
            props: () => ({
                domainId: domain.id,
                itemType,
                ...localeProps,
            }),
        },
    ]

    return (
        <div className="catalog-page">
            <PageHeader title={pageTitle} onTitleSet={appService.customizeTabTitle} />
            <SimpleTabbedView pathStyle="slash" animated={false} tabs={catalogTabs} />
        </div>
    )
})
